import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { authAPI, mfaAPI } from '../services/api';
import { User } from '../types';

// MFA pending state
export interface MFAPendingState {
  mfaToken: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean; mfaToken?: string }>;
  completeMFALogin: (mfaToken: string, code: string, isRecoveryCode?: boolean, trustDevice?: boolean, deviceName?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  refreshToken: () => Promise<boolean>;
  mfaPending: MFAPendingState | null;
  clearMFAPending: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to decode JWT and check expiration
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

// Helper function to check if token expires within next 5 minutes
const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutesFromNow = currentTime + (5 * 60);
    return payload.exp < fiveMinutesFromNow;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState<MFAPendingState | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      if (isTokenExpired(token)) {
        console.warn('🔐 Token expired on app load, clearing localStorage');
        localStorage.removeItem('auth_token');
        setLoading(false);
      } else {
        loadUser();
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Set up token refresh check every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('auth_token');
      if (token && user) {
        if (isTokenExpired(token)) {
          console.warn('🔐 Token expired, logging out user');
          logout();
        } else if (isTokenExpiringSoon(token)) {
          console.log('🔐 Token expiring soon, attempting refresh');
          refreshToken();
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [user]);

  const loadUser = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
    } catch (error: any) {
      console.error('🔐 Failed to load user profile:', error);
      if (error.response?.status === 401) {
        console.warn('🔐 Profile load failed with 401, clearing token');
        localStorage.removeItem('auth_token');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const currentToken = localStorage.getItem('auth_token');
      if (!currentToken) {
        return false;
      }

      console.log('🔐 Attempting token refresh');
      const response = await authAPI.refreshToken();
      
      // Update token in localStorage
      const { token, expires_at } = response.data;
      localStorage.setItem('auth_token', token);
      
      console.log('🔐 Token refresh successful, new token expires at:', new Date(expires_at * 1000));
      return true;
    } catch (error: any) {
      console.error('🔐 Token refresh failed:', error);
      if (error.response?.status === 401) {
        console.warn('🔐 Token refresh failed with 401, logging out');
        logout();
      }
      return false;
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ mfaRequired: boolean; mfaToken?: string }> => {
    const response = await authAPI.login(email, password);

    // Check if MFA is required (HTTP 202 response)
    if (response.status === 202 && response.data.mfa_required) {
      console.log('🔐 MFA verification required');
      setMfaPending({
        mfaToken: response.data.mfa_token,
        email: email,
      });
      return { mfaRequired: true, mfaToken: response.data.mfa_token };
    }

    // Normal login (HTTP 200)
    const { token, user } = response.data;
    console.log('🔐 Login successful, storing token');
    localStorage.setItem('auth_token', token);
    setUser(user);
    // Signal WebSocket to connect with new token
    window.dispatchEvent(new CustomEvent('auth_login'));
    return { mfaRequired: false };
  }, []);

  const completeMFALogin = useCallback(async (
    mfaToken: string,
    code: string,
    isRecoveryCode: boolean = false,
    trustDevice: boolean = false,
    deviceName?: string
  ) => {
    const response = await mfaAPI.verifyLogin(
      mfaToken,
      isRecoveryCode ? undefined : code,
      isRecoveryCode ? code : undefined,
      trustDevice,
      deviceName
    );

    const { token, user } = response.data;
    console.log('🔐 MFA verification successful, storing token');
    localStorage.setItem('auth_token', token);
    setUser(user);
    setMfaPending(null);
    // Signal WebSocket to connect with new token
    window.dispatchEvent(new CustomEvent('auth_login'));
  }, []);

  const clearMFAPending = useCallback(() => {
    setMfaPending(null);
  }, []);

  const logout = useCallback(async () => {
    console.log('🔐 Logging out user');
    // Call server-side logout to revoke the token
    try {
      await authAPI.logout();
    } catch (error) {
      // Even if server-side logout fails, proceed with client-side cleanup
      console.warn('🔐 Server-side logout failed, proceeding with client cleanup');
    }
    localStorage.removeItem('auth_token');
    setUser(null);
    // SECURITY: Signal WebSocket to disconnect immediately
    window.dispatchEvent(new CustomEvent('auth_logout'));
  }, []);

  const value = useMemo(() => ({
    user, login, completeMFALogin, logout, loading, refreshToken, mfaPending, clearMFAPending
  }), [user, login, completeMFALogin, logout, loading, refreshToken, mfaPending, clearMFAPending]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};